// Invitations list view (List archetype, invitations-settings.md): the shared
// PaginatedTableSection renders the four-way list state; the pending-row
// revoke uses the shared ConfirmModal (§4a). Presentational.

import { useState } from "react";

import type { ColumnDef } from "@tanstack/react-table";

import type { components } from "../../api/schema";
import {
  Badge,
  Button,
  Copyable,
  EmptyState,
  InlineError,
  Stack,
  Text,
} from "../../ui";
import { PaginatedTableSection } from "../shared/PaginatedTableSection";
import { ConfirmModal } from "../shared/ConfirmModal";
import { DateCell } from "../shared/DateCell";
import { UserLabel } from "../shared/userDirectory";

type Invitation = components["schemas"]["InvitationResponse"];
type InvitationStatus = components["schemas"]["InvitationStatus"];
type Team = components["schemas"]["TeamResponse"];
type User = components["schemas"]["UserResponse"];

// `invitations.status` is the CLOSED lifecycle enum — pending | accepted |
// expired (docs/03 §5.3). There is no `revoked` status: a revoked invite is a
// soft-deleted pending row (docs/05 §6.7), so it simply drops off the list.
export function statusTone(
  status: InvitationStatus,
): "neutral" | "success" | "warning" {
  if (status === "accepted") {
    return "success";
  }
  return status === "expired" ? "warning" : "neutral";
}

// The revoke confirm's target — carries the invitee email so the ConfirmModal
// can name the exact invitation without re-resolving from the row.
interface RevokeTarget {
  email: string;
  id: string;
}

function invitationColumns({
  canManage,
  onStartRevoke,
  revokingInvitationId,
  teamsById,
  usersById,
}: {
  canManage: boolean;
  onStartRevoke: (target: RevokeTarget) => void;
  revokingInvitationId?: string;
  teamsById: ReadonlyMap<string, string>;
  usersById: ReadonlyMap<string, User>;
}): Array<ColumnDef<Invitation>> {
  // Sortable-value columns carry accessors. No facet selects: role and status
  // are sortable columns, which is enough to group a list this size, and the
  // two dropdowns cost a chrome row above the table.
  return [
    {
      header: "Invitee",
      id: "invitee",
      accessorKey: "email",
      cell: ({ row }) => <Text as="span">{row.original.email}</Text>,
    },
    {
      header: "Team",
      id: "team",
      cell: ({ row }) => {
        const displayName = teamsById.get(row.original.team_id);
        return displayName ? (
          <Text as="span">{displayName}</Text>
        ) : (
          <Copyable label="Team ID" value={row.original.team_id} />
        );
      },
    },
    {
      header: "Role",
      accessorKey: "member_role",
      cell: ({ row }) => <Badge>{row.original.member_role}</Badge>,
    },
    {
      header: "Status",
      accessorKey: "status",
      cell: ({ row }) => (
        <Badge tone={statusTone(row.original.status)}>
          {row.original.status}
        </Badge>
      ),
    },
    {
      header: "Invited by",
      id: "invited_by",
      cell: ({ row }) => (
        <UserLabel
          compact
          fallbackId={row.original.created_by}
          user={usersById.get(row.original.created_by)}
        />
      ),
    },
    {
      header: "Expires",
      accessorKey: "expires_at",
      cell: ({ row }) => <DateCell value={row.original.expires_at} />,
    },
    {
      header: "Accepted",
      cell: ({ row }) =>
        row.original.accepted_at ? (
          <DateCell value={row.original.accepted_at} />
        ) : (
          <Text as="span" variant="muted">
            —
          </Text>
        ),
    },
    {
      header: "Accepted by",
      id: "accepted_by",
      cell: ({ row }) =>
        row.original.accepted_by ? (
          <UserLabel
            compact
            fallbackId={row.original.accepted_by}
            user={usersById.get(row.original.accepted_by)}
          />
        ) : (
          <Text as="span" variant="muted">
            —
          </Text>
        ),
    },
    {
      header: "Actions",
      cell: ({ row }) => {
        const invitation = row.original;
        // Revoke = soft-delete of a pending invite (docs/05 §6.7). There is no
        // PATCH and no `revoked` status; accepted/expired rows are terminal.
        if (!canManage || invitation.status !== "pending") {
          return (
            <Text as="span" variant="muted">
              —
            </Text>
          );
        }

        return (
          <Button
            disabled={revokingInvitationId != null}
            size="sm"
            type="button"
            variant="danger"
            onClick={() => {
              onStartRevoke({ email: invitation.email, id: invitation.id });
            }}
          >
            Revoke
          </Button>
        );
      },
    },
  ];
}

export interface InvitationsTableProps {
  canManage: boolean;
  hasNextPage: boolean;
  invitations: Invitation[];
  isFetchingNextPage: boolean;
  isPending: boolean;
  listErrorMessage?: string;
  listStateKey: string;
  onLoadMore: () => void;
  onOpenInvite: () => void;
  onResetRevoke: () => void;
  onRevoke: (id: string) => Promise<void>;
  revokeErrorInvitationId?: string;
  revokeErrorMessage?: string;
  revokingInvitationId?: string;
  teams: Team[];
  usersById: ReadonlyMap<string, User>;
  usersErrorMessage?: string;
}

export function InvitationsTable({
  canManage,
  hasNextPage,
  invitations,
  isFetchingNextPage,
  isPending,
  listErrorMessage,
  listStateKey,
  onLoadMore,
  onOpenInvite,
  onResetRevoke,
  onRevoke,
  revokeErrorInvitationId,
  revokeErrorMessage,
  revokingInvitationId,
  teams,
  usersById,
  usersErrorMessage,
}: InvitationsTableProps) {
  const [revokeTarget, setRevokeTarget] = useState<RevokeTarget | null>(null);

  const closeRevoke = (): void => {
    onResetRevoke();
    setRevokeTarget(null);
  };

  const confirmRevoke = async (id: string): Promise<void> => {
    try {
      await onRevoke(id);
      setRevokeTarget(null);
    } catch {
      // The parent mutation owns error presentation; the confirm stays open
      // (revokeTarget untouched) so the modal can surface the error inline.
    }
  };

  const columns = invitationColumns({
    canManage,
    onStartRevoke: (target) => {
      onResetRevoke();
      setRevokeTarget(target);
    },
    revokingInvitationId,
    teamsById: new Map(teams.map((team) => [team.id, team.display_name])),
    usersById,
  });

  const isRevoking =
    revokeTarget != null && revokingInvitationId === revokeTarget.id;
  const revokeModalErrorMessage =
    revokeTarget != null &&
    revokeErrorInvitationId === revokeTarget.id &&
    revokeErrorMessage
      ? revokeErrorMessage
      : null;

  return (
    <Stack fill gap={3}>
      {usersErrorMessage ? (
        <InlineError>{usersErrorMessage}</InlineError>
      ) : null}
      <PaginatedTableSection
        ariaLabel="Invitations"
        columns={columns}
        emptyState={
          <EmptyState
            action={
              canManage ? (
                <Button type="button" onClick={onOpenInvite}>
                  Invite
                </Button>
              ) : undefined
            }
            title="No invitations"
          />
        }
        boundedColumns={["invitee", "invited_by", "accepted_by", "team"]}
        className="marker-data-table--narrow-bounds"
        enableSorting
        errorMessage={listErrorMessage}
        getRowId={(invitation) => invitation.id}
        hasNextPage={hasNextPage}
        isFetchingNextPage={isFetchingNextPage}
        isPending={isPending}
        items={invitations}
        listStateKey={listStateKey}
        skeletonLabel="Loading invitations"
        virtual={{ fill: true }}
        onLoadMore={onLoadMore}
      />
      {revokeTarget ? (
        <ConfirmModal
          ariaLabel="Revoke invitation?"
          confirmLabel="Revoke invitation"
          errorMessage={revokeModalErrorMessage}
          isPending={isRevoking}
          pendingLabel="Revoking..."
          sentence={
            `Revoke invitation for ${revokeTarget.email}? ` +
            "This prevents the pending invitation from being accepted."
          }
          onCancel={closeRevoke}
          onConfirm={() => {
            void confirmRevoke(revokeTarget.id);
          }}
        />
      ) : null}
    </Stack>
  );
}
